import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';

function CatalogFormModal({ config, row, onSave, onClose }) {
  const isEdit = !!row;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState({});

  useEffect(() => {
    // Initialize form values
    const initial = {};
    config.fields.forEach((field) => {
      if (isEdit && row[field.key] != null) {
        initial[field.key] = String(row[field.key]);
      } else {
        initial[field.key] =
          field.default != null
            ? String(field.default)
            : field.type === 'color'
              ? '#4CAF50'
              : '';
      }
    });
    setForm(initial);

    // Fetch FK lookup data
    const fkFields = config.fields.filter((f) => f.type === 'fk');
    if (fkFields.length === 0) return;

    Promise.all(
      fkFields.map(async (field) => {
        const { data: activeOpts } = await supabase
          .from(field.fkTable)
          .select(`id, ${field.fkLabel}`)
          .eq('active', true)
          .order(field.fkLabel);

        let opts = activeOpts || [];

        // If editing and current FK target is inactive, include it so the select shows correctly
        if (isEdit && row[field.key]) {
          const currentId = row[field.key];
          if (!opts.find((o) => o.id === currentId)) {
            const { data: current } = await supabase
              .from(field.fkTable)
              .select(`id, ${field.fkLabel}`)
              .eq('id', currentId)
              .single();
            if (current) {
              current[field.fkLabel] += ' (Inactivo)';
              opts = [current, ...opts];
            }
          }
        }

        return { table: field.fkTable, opts };
      })
    ).then((results) => {
      const newLookups = {};
      results.forEach((r) => {
        newLookups[r.table] = r.opts;
      });
      setLookups(newLookups);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
    if (submitError) setSubmitError('');
  }

  function validate() {
    const e = {};
    config.fields.forEach((field) => {
      if (field.required && !form[field.key] && form[field.key] !== 0) {
        e[field.key] = 'Este campo es requerido.';
      }
    });
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      await onSave(form);
    } catch (err) {
      /* 23505 = unique_violation (e.g. billing_types has a case-insensitive unique name) */
      setSubmitError(err?.code === '23505'
        ? 'Ya existe un registro con ese nombre.'
        : 'No se pudo guardar. Verifica los datos e intenta de nuevo.');
      setLoading(false);
    }
  }

  function renderField(field) {
    const val = form[field.key] ?? '';
    const hasError = !!errors[field.key];

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            className={`cat-input${hasError ? ' cat-input--error' : ''}`}
            value={val}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className={`cat-input${hasError ? ' cat-input--error' : ''}`}
            value={val}
            onChange={(e) => handleChange(field.key, e.target.value)}
            step={field.step || '1'}
            min={field.min || ''}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className={`cat-input${hasError ? ' cat-input--error' : ''}`}
            value={val}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        );

      case 'color':
        return (
          <div className="cat-color-input-wrap">
            <input
              type="color"
              className="cat-color-picker"
              value={val || '#4CAF50'}
              onChange={(e) => handleChange(field.key, e.target.value)}
            />
            <input
              type="text"
              className={`cat-input${hasError ? ' cat-input--error' : ''}`}
              value={val}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder="#000000"
            />
          </div>
        );

      case 'fk':
        return (
          <select
            className={`cat-select${hasError ? ' cat-input--error' : ''}`}
            value={val}
            onChange={(e) => handleChange(field.key, e.target.value)}
          >
            <option value="">Seleccionar…</option>
            {(lookups[field.fkTable] || []).map((opt) => (
              <option key={opt.id} value={String(opt.id)}>
                {opt[field.fkLabel]}
              </option>
            ))}
          </select>
        );

      case 'select':
        return (
          <select
            className={`cat-select${hasError ? ' cat-input--error' : ''}`}
            value={val}
            onChange={(e) => handleChange(field.key, e.target.value)}
          >
            <option value="">Seleccionar…</option>
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      default:
        return null;
    }
  }

  return (
    <>
      <div className="cat-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="cat-modal" role="dialog" aria-modal="true" aria-labelledby="cat-modal-title">
        <h2 id="cat-modal-title" className="cat-modal-title">
          {isEdit ? `Editar ${config.singular}` : `Nuevo ${config.singular}`}
        </h2>
        <form className="cat-modal-form" onSubmit={handleSubmit} noValidate>
          {config.fields.map((field) => (
            <div className="cat-form-group" key={field.key}>
              <label className="cat-form-label">
                {field.label}
                {field.required && <span className="cat-required"> *</span>}
              </label>
              {renderField(field)}
              {errors[field.key] && (
                <span className="cat-form-error">{errors[field.key]}</span>
              )}
            </div>
          ))}

          {submitError && (
            <div className="cat-error-banner" role="alert">
              {submitError}
            </div>
          )}

          <div className="cat-modal-actions">
            <button
              type="button"
              className="cat-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="cat-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="cat-spinner" aria-hidden="true" />
                  Guardando…
                </>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default CatalogFormModal;
